/**
 * badge.tsx - Chips de status/regime da biblioteca base.
 *
 * Badge por tom semantico (nunca cor hex por prop): default com contorno,
 * soft com fundo --color-status-*-soft. Radius --radius-pill.
 * StatusBadge/RegimenBadge mantem compatibilidade com a semente contas-ui,
 * re-tokenizados: verde/ambar/vermelho pelos tokens --color-status-*;
 * ciano "a vencer" e violeta "provisionado" viram neutro (status-gray),
 * conforme o mapa de cor da repaginacao (§2.2).
 */

import type { ReactNode } from "react";
import { REGIMEN_LABELS, type Regimen } from "@/domain/cashflow";
import type { StatusTitulo } from "@/features/contas/contas-types";

export type BadgeTone = "neutral" | "green" | "amber" | "red" | "info";

const TONE_COLOR: Record<BadgeTone, string> = {
  neutral: "var(--color-status-gray)",
  green: "var(--color-status-green)",
  amber: "var(--color-status-amber)",
  red: "var(--color-status-red)",
  info: "var(--color-accent)",
};

const TONE_SOFT_BG: Record<BadgeTone, string> = {
  neutral: "var(--color-status-gray-soft)",
  green: "var(--color-status-green-soft)",
  amber: "var(--color-status-amber-soft)",
  red: "var(--color-status-red-soft)",
  info: "var(--color-accent-subtle)",
};

export function Badge({
  tone = "neutral",
  soft = false,
  children,
}: {
  tone?: BadgeTone;
  soft?: boolean;
  children: ReactNode;
}) {
  const color = TONE_COLOR[tone];
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        padding: "1px 7px",
        borderRadius: "var(--radius-pill)",
        background: soft ? TONE_SOFT_BG[tone] : "transparent",
        border: soft ? "1px solid transparent" : `1px solid ${color}`,
        color,
        fontFamily: '"IBM Plex Mono", monospace',
        fontSize: "0.5625rem",
        fontWeight: 700,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// StatusBadge - estado de negocio do titulo (compat com contas-types)
// ---------------------------------------------------------------------------

const STATUS_LABEL: Record<StatusTitulo, string> = {
  a_vencer: "A vencer",
  vencido: "Vencido",
  pago: "Pago",
  recebido: "Recebido",
  parcial: "Parcial",
  glosado: "Glosado",
  em_recurso: "Em recurso",
  agendado: "Agendado",
};

const STATUS_TONE: Record<StatusTitulo, BadgeTone> = {
  a_vencer: "neutral",
  vencido: "red",
  pago: "green",
  recebido: "green",
  parcial: "amber",
  glosado: "amber",
  em_recurso: "neutral",
  agendado: "neutral",
};

export function StatusBadge({ status }: { status: StatusTitulo }) {
  return (
    <Badge tone={STATUS_TONE[status]} soft>
      {STATUS_LABEL[status]}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// RegimenBadge - regime do lancamento (labels oficiais de domain/cashflow;
// cores APENAS por tom semantico, sem os hex de REGIMEN_COLORS)
// ---------------------------------------------------------------------------

const REGIMEN_TONE: Record<Regimen, BadgeTone> = {
  realizado: "green",
  contratado: "info",
  provisionado: "neutral",
  projetado: "neutral",
  meta: "amber",
  cenario: "neutral",
};

export function RegimenBadge({ regimen }: { regimen: string }) {
  const tone: BadgeTone = Object.prototype.hasOwnProperty.call(REGIMEN_TONE, regimen)
    ? REGIMEN_TONE[regimen as Regimen]
    : "neutral";
  const label: string = Object.prototype.hasOwnProperty.call(REGIMEN_LABELS, regimen)
    ? REGIMEN_LABELS[regimen as Regimen]
    : regimen;
  return <Badge tone={tone}>{label}</Badge>;
}
